import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { User, Mail, Phone, Calendar, Package, MapPin, LogOut, Save, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { heroImages } from '../mock/data';

const Account = () => {
  const { user, loading, logout, updateProfile } = useAuth();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '' });
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#c8102e]" /></div>;
  if (!user) return <Navigate to="/login" replace />;

  const startEdit = () => { setForm({ name: user.name, phone: user.phone || '' }); setEditing(true); };
  const cancel = () => setEditing(false);
  const save = async () => {
    setBusy(true);
    try {
      await updateProfile(form);
      toast({ title: 'Profile updated', description: 'Your changes have been saved.' });
      setEditing(false);
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || 'Please try again.' });
    } finally { setBusy(false); }
  };

  return (
    <div>
      <section className="relative h-[300px] flex items-end bg-black overflow-hidden">
        <img src={heroImages.counter} alt="Account" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pb-10">
          <div className="flex items-center gap-3 mb-3"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow text-white">My Account</span></div>
          <h1 className="text-white font-serif-display text-4xl md:text-5xl font-bold leading-[1.05]">Welcome, {user.name.split(' ')[0]}.</h1>
        </div>
      </section>

      <section className="py-16 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
          <aside className="lg:col-span-1">
            <div className="bg-white p-8 border border-neutral-200">
              <div className="w-20 h-20 rounded-full bg-[#c8102e] flex items-center justify-center text-white font-serif-display text-3xl font-bold mb-4">
                {user.name.charAt(0).toUpperCase()}
              </div>
              <h3 className="font-serif-display text-2xl font-bold text-black mb-1">{user.name}</h3>
              <p className="text-sm text-neutral-500 mb-6">{user.email}</p>
              <button onClick={logout} className="w-full flex items-center justify-center gap-2 border border-black hover:bg-black hover:text-white h-11 text-[12px] tracking-[0.15em] uppercase font-semibold transition-colors">
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            </div>
          </aside>

          <main className="lg:col-span-2 space-y-6">
            <div className="bg-white p-8 border border-neutral-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Profile</span></div>
                  <h2 className="font-serif-display text-2xl font-bold text-black">Personal Information</h2>
                </div>
                {!editing && <Button onClick={startEdit} className="bg-black hover:bg-[#c8102e] text-white h-10 px-5 text-[12px] tracking-[0.15em] uppercase font-semibold">Edit</Button>}
              </div>

              {!editing ? (
                <div className="space-y-5">
                  <Row icon={User} label="Full Name" value={user.name} />
                  <Row icon={Mail} label="Email" value={user.email} />
                  <Row icon={Phone} label="Phone" value={user.phone || 'Not set'} />
                  <Row icon={Calendar} label="Member Since" value={new Date(user.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })} />
                </div>
              ) : (
                <div className="space-y-4">
                  <Field label="Full Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12" /></Field>
                  <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12" placeholder="+39 ..." /></Field>
                  <div className="flex gap-3 pt-2">
                    <Button onClick={save} disabled={busy} className="bg-[#c8102e] hover:bg-black text-white h-12 px-6 text-[12px] tracking-[0.15em] uppercase font-semibold">
                      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Changes
                    </Button>
                    <Button onClick={cancel} variant="outline" className="border-neutral-300 h-12 px-6 text-[12px] tracking-[0.15em] uppercase font-semibold">Cancel</Button>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link to="/find-branch" className="group bg-black text-white p-8 border border-black hover:bg-[#c8102e] hover:border-[#c8102e] transition-colors">
                <MapPin className="w-6 h-6 text-[#c8102e] group-hover:text-white mb-4 transition-colors" />
                <h3 className="font-serif-display text-xl font-bold mb-2">Find nearest branch</h3>
                <p className="text-sm text-white/70">Locate the closest Lanka Express office to you in seconds.</p>
              </Link>
              <Link to="/contact" className="group bg-white p-8 border border-neutral-200 hover:border-black transition-colors">
                <Package className="w-6 h-6 text-[#c8102e] mb-4" />
                <h3 className="font-serif-display text-xl font-bold text-black mb-2">Contact support</h3>
                <p className="text-sm text-neutral-600">Reach out to our dedicated customer service team anytime.</p>
              </Link>
            </div>
          </main>
        </div>
      </section>
    </div>
  );
};

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-4 py-3 border-b border-neutral-100 last:border-b-0">
    <div className="w-10 h-10 bg-neutral-50 flex items-center justify-center"><Icon className="w-4 h-4 text-[#c8102e]" /></div>
    <div className="flex-1">
      <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold mb-0.5">{label}</div>
      <div className="text-sm font-medium text-black">{value}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] tracking-[0.25em] uppercase text-neutral-600 font-semibold mb-2">{label}</label>
    {children}
  </div>
);

export default Account;
